import { Text } from '@/components/DesignSystem';
import { Routes } from '@/constants/Routes';
import { useAuthentication } from '@/contexts/dataSync/AuthenticationContext';
import { useSnackbar } from '@/providers/GlobalSnackbarProvider';
import { Color } from '@/themes/MUITheme/palette';
import { UserApi } from '@/utilities/api/UserApi';
import { LocalAccountList } from '@/utilities/LocalAccountList';
import { Autocomplete, TextField } from '@mui/material';
import { UserType } from '@shared/enums';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

interface SwitcherOption {
  color?: Color,
  id: string,
  label: string,
}

export const UserSwitcher = () => {
  const { t } = useTranslation();
  const { user, refreshUser, logout } = useAuthentication();
  const { openSnackbar } = useSnackbar();
  const navigate = useNavigate();

  if (!LocalAccountList.isActive()) { return null; }

  const addAccount = `[${t($ => $.shared.userSwitcher.addAccount)}]`;
  const clearAll = `[${t($ => $.common.actions.clearAll)}]`;
  const colors: {[type in UserType]: Color} = {
    [UserType.Farmer]: 'success',
    [UserType.Internal]: 'warning',
    [UserType.RetailerAdmin]: 'info',
    [UserType.SalesPerson]: 'info',
  };

  const accountOptions: SwitcherOption[] = LocalAccountList.list().map((account) => ({
    color: colors[account.type],
    id: account.id,
    label: account.name,
  }));
  const actionOptions: SwitcherOption[] = [addAccount, clearAll].map((action) => ({
    id: action,
    label: action,
  }));
  const currentAccount = accountOptions.find((option) => option.id === user?.id);

  const onSelectAccount = async (value: string) => {
    if (value === addAccount || value === clearAll) {
      if (value === clearAll) {
        LocalAccountList.clear();
      }
      // Revoking the Cognito session would end the copy the switcher keeps of it.
      await logout({ keepCognitoSession: value === addAccount, removeFromAccountSwitcher: false });
      navigate(Routes.LOGIN, { replace: true });
    } else {
      const { ticket, token } = LocalAccountList.get(value);
      try {
        await UserApi.switchAccount({ ticket, token });
      } catch {
        openSnackbar(t($ => $.shared.userSwitcher.switchFailed));
        return;
      }
      await refreshUser();
    }
  };

  return (
    <Autocomplete
      data-account-id={currentAccount?.id}
      data-testid="user-switcher"
      disableClearable
      getOptionLabel={(option) => option.label}
      isOptionEqualToValue={(option, selected) => option.id === selected.id}
      onChange={(_, option) => {
        if (option && option.id !== user?.id) {
          void onSelectAccount(option.id);
        }
      }}
      options={[...accountOptions, ...actionOptions]}
      renderInput={(params) => (
        <TextField {...params} label={t($ => $.shared.userSwitcher.currentUser)} />
      )}
      renderOption={(props, option) => (
        <li {...props} data-testid={'user-switcher-' + option.id} key={option.id}>
          <Text color={option.color}>{option.label}</Text>
        </li>
      )}
      size="small"
      sx={{
        width: 200,
        '& .MuiInputBase-input': { color: currentAccount?.color && `${currentAccount.color}.main` },
      }}
      value={currentAccount ?? actionOptions[0]}
    />
  );
};
